import React, { useState } from 'react';
import { User, Mail, Phone, MapPin, CheckCircle } from 'react-feather';

export interface CustomerInfoFormValues {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  sms_consent: boolean;
  /** If set, backend will send a 6-digit code and wait for confirmation
   *  before attaching the request to an existing customer. Purely optional —
   *  omitting it keeps the frictionless guest flow. */
  verify_channel?: 'sms' | 'email' | null;
}

export interface CustomerInfoFormPrefill {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

interface CustomerInfoFormProps {
  orgName: string;
  prefill?: CustomerInfoFormPrefill;
  onSubmit: (values: CustomerInfoFormValues) => void;
  disabled?: boolean;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1.5px solid #e5e7eb',
  fontSize: 'var(--agent-field-font, 0.9rem)', // 16px on phones (AgentChat.scss): no iOS focus zoom
  fontFamily: 'inherit',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: '0.78rem',
  fontWeight: 600,
  color: '#4b5563',
};

const CustomerInfoForm: React.FC<CustomerInfoFormProps> = ({
  orgName,
  prefill = {},
  onSubmit,
  disabled = false,
}) => {
  const [first_name, setFirstName] = useState(prefill.first_name ?? '');
  const [last_name, setLastName] = useState(prefill.last_name ?? '');
  const [email, setEmail] = useState(prefill.email ?? '');
  const [phone, setPhone] = useState(prefill.phone ?? '');
  const [address, setAddress] = useState(prefill.address ?? '');
  const [city, setCity] = useState(prefill.city ?? '');
  const [state, setState] = useState(prefill.state ?? '');
  const [zip_code, setZipCode] = useState(prefill.zip_code ?? '');
  const [sms_consent, setSmsConsent] = useState(false);
  const [verifyEnabled, setVerifyEnabled] = useState(false);
  const [verifyChannel, setVerifyChannel] = useState<'sms' | 'email'>('sms');
  const [touched, setTouched] = useState(false);

  const missing =
    !first_name.trim() ||
    !last_name.trim() ||
    !email.trim() ||
    !phone.trim() ||
    !address.trim() ||
    !city.trim() ||
    !state.trim() ||
    !zip_code.trim();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (missing) return;
    onSubmit({
      first_name: first_name.trim(),
      last_name: last_name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      address: address.trim(),
      city: city.trim(),
      state: state.trim(),
      zip_code: zip_code.trim(),
      sms_consent,
      verify_channel: verifyEnabled ? verifyChannel : null,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="service-request-card service-request-card--customer"
    >
      <div className="service-request-card__header">
        <div className="service-request-card__icon service-request-card__icon--customer">
          <User size={20} />
        </div>
        <h3 className="service-request-card__title">Your Information</h3>
      </div>

      <div className="service-request-card__content" style={{ gap: 10 }}>
        <p className="service-request-card__description" style={{ marginBottom: 4 }}>
          We just need a few details so we can follow up with you.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label style={labelStyle}>
            <span>First name</span>
            <input
              value={first_name}
              onChange={(e) => setFirstName(e.target.value)}
              style={inputStyle}
              disabled={disabled}
              autoComplete="given-name"
            />
          </label>
          <label style={labelStyle}>
            <span>Last name</span>
            <input
              value={last_name}
              onChange={(e) => setLastName(e.target.value)}
              style={inputStyle}
              disabled={disabled}
              autoComplete="family-name"
            />
          </label>
        </div>

        <label style={labelStyle}>
          <span><Mail size={12} /> Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
            disabled={disabled}
            autoComplete="email"
          />
        </label>

        <label style={labelStyle}>
          <span><Phone size={12} /> Mobile phone</span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            style={inputStyle}
            disabled={disabled}
            autoComplete="tel"
          />
        </label>

        <label style={labelStyle}>
          <span><MapPin size={12} /> Street address</span>
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            style={inputStyle}
            disabled={disabled}
            autoComplete="street-address"
          />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
          <label style={labelStyle}>
            <span>City</span>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              style={inputStyle}
              disabled={disabled}
              autoComplete="address-level2"
            />
          </label>
          <label style={labelStyle}>
            <span>State</span>
            <input
              value={state}
              onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
              maxLength={2}
              style={inputStyle}
              disabled={disabled}
              autoComplete="address-level1"
            />
          </label>
          <label style={labelStyle}>
            <span>ZIP</span>
            <input
              value={zip_code}
              onChange={(e) => setZipCode(e.target.value)}
              style={inputStyle}
              disabled={disabled}
              autoComplete="postal-code"
            />
          </label>
        </div>

        <div
          style={{
            marginTop: 4,
            padding: '10px 12px',
            background: '#eef2ff',
            border: '1px solid #c7d2fe',
            borderRadius: 8,
            fontSize: '0.82rem',
            color: '#1e3a8a',
          }}
        >
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={verifyEnabled}
              onChange={(e) => setVerifyEnabled(e.target.checked)}
              disabled={disabled}
              style={{ marginTop: 3, flexShrink: 0 }}
            />
            <span>
              <strong>Have an account with us?</strong> Verify to link your existing equipment (optional).
              {verifyEnabled && (
                <span
                  style={{
                    display: 'flex',
                    gap: 12,
                    marginTop: 6,
                    fontSize: '0.78rem',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="verify_channel"
                      value="sms"
                      checked={verifyChannel === 'sms'}
                      onChange={() => setVerifyChannel('sms')}
                      disabled={disabled}
                    />
                    Text me a code
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="verify_channel"
                      value="email"
                      checked={verifyChannel === 'email'}
                      onChange={() => setVerifyChannel('email')}
                      disabled={disabled}
                    />
                    Email me a code
                  </label>
                </span>
              )}
            </span>
          </label>
        </div>

        <div
          style={{
            marginTop: 4,
            padding: '10px 12px',
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            fontSize: '0.82rem',
            color: '#1f2937',
          }}
        >
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={sms_consent}
              onChange={(e) => setSmsConsent(e.target.checked)}
              disabled={disabled}
              style={{ marginTop: 3, flexShrink: 0 }}
            />
            <span>
              I agree to receive SMS messages about this service request.
              <span style={{ display: 'block', marginTop: 4, fontSize: '0.72rem', color: '#6b7280', lineHeight: 1.45 }}>
                I consent to receive automated SMS messages from <strong>{orgName}</strong> (powered by Opervia)
                regarding service request updates, scheduling, and completion notifications.
                Message frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.
                Consent is not a condition of purchase.{' '}
                <a
                  href="https://opervia.com/sms-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#2563eb' }}
                >
                  SMS Terms
                </a>
                {' | '}
                <a
                  href="https://opervia.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#2563eb' }}
                >
                  Privacy Policy
                </a>
              </span>
            </span>
          </label>
        </div>

        {touched && missing && (
          <div style={{ color: '#dc2626', fontSize: '0.78rem', marginTop: 4 }}>
            Please fill in every field before continuing.
          </div>
        )}
      </div>

      <div className="service-request-card__actions">
        <button
          className="service-request-card__btn service-request-card__btn--primary"
          type="submit"
          disabled={disabled}
        >
          <CheckCircle size={16} />
          Submit Information
        </button>
      </div>
    </form>
  );
};

export default CustomerInfoForm;
